import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Space,
  Popconfirm,
  Typography,
  Row,
  Col,
  Tabs,
  Divider,
  // App,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  QrcodeOutlined,
  EnvironmentOutlined,
  AimOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import QRCode from "qrcode.react";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import LoadingSpinner from "../components/UI/LoadingSpinner";
import MapPicker from "../components/UI/MapPicker";

const { Title } = Typography;
const { TabPane } = Tabs;

const Checkpoints = () => {
  const { user } = useAuth();
  // const { message: messageApi } = App.useApp();
  const [messageApi, contextHolder] = message.useMessage();
  const [checkpoints, setCheckpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [editingCheckpoint, setEditingCheckpoint] = useState(null);
  const [selectedCheckpoint, setSelectedCheckpoint] = useState(null);
  const [mapPosition, setMapPosition] = useState({
    lat: 13.096092,
    lng: 100.964311,
  });
  const [form] = Form.useForm();

  useEffect(() => {
    if (user?.current_company_id) {
      fetchCheckpoints();
    }
  }, [user?.current_company_id]); // Re-fetch when company changes

  const fetchCheckpoints = async () => {
    try {
      setLoading(true);
      const response = await api.get("/checkpoints");

      if (response.data.success) {
        setCheckpoints(response.data.data);
      }
    } catch (error) {
      console.error("Fetch checkpoints error:", error);
      messageApi.error("ไม่สามารถโหลดข้อมูลจุดตรวจได้");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCheckpoint(null);
    form.resetFields();
    setMapPosition({ lat: 13.096092, lng: 100.964311 });
    setModalVisible(true);
  };

  const handleEdit = (checkpoint) => {
    setEditingCheckpoint(checkpoint);
    form.setFieldsValue(checkpoint);
    setMapPosition({
      lat: parseFloat(checkpoint.latitude) || 13.096092,
      lng: parseFloat(checkpoint.longitude) || 100.964311,
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      let response;

      // Include map coordinates in the submission
      const submitData = {
        ...values,
        latitude: mapPosition.lat,
        longitude: mapPosition.lng,
      };

      if (editingCheckpoint) {
        response = await api.put(
          `/checkpoints/${editingCheckpoint.id}`,
          submitData
        );
      } else {
        response = await api.post("/checkpoints", submitData);
      }

      if (response.data.success) {
        messageApi.success(
          `บันทึกข้อมูลจุดตรวจสำเร็จ!`
        );
        setModalVisible(false);
        fetchCheckpoints();
      }
    } catch (error) {
      console.error("Submit checkpoint error:", error);
      messageApi.error(
        error.response?.data?.message ||
        `ไม่สามารถบันทึกข้อมูลจุดตรวจได้`
      );
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await api.delete(`/checkpoints/${id}`);

      if (response.data.success) {
        messageApi.success("ลบจุดตรวจเรียบร้อยแล้ว!");
        fetchCheckpoints();
      }
    } catch (error) {
      console.error("Delete checkpoint error:", error);
      messageApi.error(
        error.response?.data?.message || "ไม่สามารถลบจุดตรวจได้"
      );
    }
  };

  const showQRCode = async (checkpoint) => {
    try {
      const response = await api.get(`/checkpoints/${checkpoint.id}/qr`);

      if (response.data.success) {
        setSelectedCheckpoint({
          ...checkpoint,
          qr_image: response.data.data.qr_image,
        });
        setQrModalVisible(true);
      }
    } catch (error) {
      console.error("Generate QR code error:", error);
      messageApi.error("ไม่สามารถสร้าง QR Code ได้");
    }
  };

  const handleMapPositionChange = (position) => {
    setMapPosition(position);
    // Update form fields when map position changes
    form.setFieldsValue({
      latitude: position.lat,
      longitude: position.lng,
    });
  };

  function processCanvas(source, checkpoint) {
    const qrSize = source.width || 250;
    const padding = 50;
    const textHeight = 80; // สำหรับชื่อจุดตรวจ + พิกัดด้านล่าง
    const companyTextHeight = 40; // พื้นที่สำหรับชื่อบริษัทด้านบน

    const totalWidth = Math.max(qrSize + padding * 2, 400);
    const totalHeight = companyTextHeight + qrSize + padding * 2 + textHeight;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = totalWidth;
    canvas.height = totalHeight;

    // พื้นหลัง
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, totalWidth, totalHeight);

    // ==== ชื่อบริษัท ====
    if (checkpoint.company_name) {
      ctx.fillStyle = "#000000";
      ctx.font = 'bold 20px "Noto Sans Thai", "Tahoma", "sans-serif"'; // ✅ รองรับไทย
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(checkpoint.company_name, totalWidth / 2, 20);
    }

    // ==== คำนวณตำแหน่ง QR Code ====
    const qrX = (totalWidth - qrSize) / 2;
    const qrY = companyTextHeight + padding;

    // วาด QR
    ctx.drawImage(source, qrX, qrY, qrSize, qrSize);

    // วาดกรอบ
    ctx.strokeStyle = "#e8e8e8";
    ctx.lineWidth = 2;
    ctx.strokeRect(qrX - 10, qrY - 10, qrSize + 20, qrSize + 20);

    // ==== ชื่อจุดตรวจ ====
    ctx.fillStyle = "#1890ff";
    ctx.font = "bold 24px Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    ctx.fillText(checkpoint.name, totalWidth / 2, qrY + qrSize + 40);

    // ==== พิกัด ====
    ctx.fillStyle = "#666666";
    ctx.font = "14px Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      `Lat: ${checkpoint.latitude}, Lng: ${checkpoint.longitude}`,
      totalWidth / 2,
      qrY + qrSize + 65
    );

    // ดาวน์โหลด
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = `QR-${checkpoint.name.replace(
        /[^a-zA-Z0-9ก-๙\\s]/g,
        "_"
      )}.png`;
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      messageApi.success(
        `ดาวน์โหลด QR Code ของ ${checkpoint.name} เรียบร้อยแล้ว`
      );
    }, "image/png");
  }

  // ฟังก์ชันสำหรับดาวน์โหลด QR Code เป็นรูปภาพ
  const downloadQRCode = (checkpoint) => {
    try {
      setTimeout(() => {
        let modalQrCanvas = document.querySelector(".ant-modal canvas");

        if (modalQrCanvas) {
          // ✅ ถ้ามี canvas (qrcode.react)
          processCanvas(modalQrCanvas, checkpoint);
        } else if (checkpoint.qr_image) {
          // ✅ ถ้าเป็น <img> (base64 หรือ url)
          const img = new Image();
          img.crossOrigin = "anonymous"; // กัน CORS ถ้าเป็น URL
          img.onload = () => {
            processCanvas(img, checkpoint);
          };
          img.src = checkpoint.qr_image;
        } else {
          messageApi.error("ไม่พบ QR Code ที่จะดาวน์โหลด กรุณาลองใหม่อีกครั้ง");
        }
      }, 500);
    } catch (error) {
      console.error("Download QR Code error:", error);
      messageApi.error("ไม่สามารถดาวน์โหลด QR Code ได้");
    }
  };

  const columns = [
    {
      title: "ชื่อจุดตรวจ",
      dataIndex: "name",
      key: "name",
      render: (text) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <AimOutlined style={{ color: "#52c41a", fontSize: 16 }} />
          <strong style={{ color: "#1890ff" }}>{text}</strong>
        </div>
      ),
      width: "30%",
      minWidth: 200,
      ellipsis: true,
    },
    {
      title: "ตำแหน่ง (GPS)",
      key: "location",
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <span
            style={{ fontSize: 13, color: "#666", fontFamily: "monospace" }}
          >
            <strong style={{ color: "#1890ff" }}>Lat:</strong>{" "}
            {parseFloat(record.latitude).toFixed(6)}
          </span>
          <span
            style={{ fontSize: 13, color: "#666", fontFamily: "monospace" }}
          >
            <strong style={{ color: "#1890ff" }}>Lng:</strong>{" "}
            {parseFloat(record.longitude).toFixed(6)}
          </span>
        </Space>
      ),
      width: "25%",
      minWidth: 180,
    },
    {
      title: "QR Code",
      key: "qr_code",
      render: (_, record) => (
        <Button
          type="primary"
          ghost
          icon={<QrcodeOutlined />}
          onClick={() => showQRCode(record)}
          size="small"
        >
          ดู QR
        </Button>
      ),
      width: "15%",
      minWidth: 100,
      align: "center",
    },
    {
      title: "การจัดการ",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
            style={{ color: "#1890ff" }}
          >
            แก้ไข
          </Button>
          <Popconfirm
            title="ต้องการลบจุดตรวจนี้?"
            description="การลบจะไม่สามารถย้อนกลับได้"
            onConfirm={() => handleDelete(record.id)}
            okText="ลบ"
            cancelText="ยกเลิก"
            okType="danger"
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small">
              ลบ
            </Button>
          </Popconfirm>
        </Space>
      ),
      width: "30%",
      minWidth: 160,
      align: "center",
    },
  ];

  if (loading) {
    return <LoadingSpinner tip="กำลังโหลดข้อมูลจุดตรวจ..." />;
  }

  return (
    <div>
      {contextHolder}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title
            level={2}
            style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}
          >
            <EnvironmentOutlined style={{ color: "#52c41a" }} />
            จัดการจุดตรวจ
          </Title>
          <p style={{ margin: "8px 0 0 0", color: "#666", fontSize: 14 }}>
            สร้างและจัดการจุดตรวจในระบบ รวมทั้งสร้าง QR Code สำหรับแต่ละจุด
          </p>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            size="large"
          >
            เพิ่มจุดตรวจ
          </Button>
        </Col>
      </Row>

      <Card
        styles={{
          body: {
            padding: "16px",
          },
        }}
      >
        <Table
          columns={columns}
          dataSource={checkpoints}
          rowKey="id"
          scroll={{ x: 800 }}
          size="middle"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จาก ${total} จุดตรวจ`,
            pageSizeOptions: ["5", "10", "20", "50"],
          }}
          rowClassName={(record, index) =>
            index % 2 === 0 ? "table-row-light" : "table-row-dark"
          }
        />
      </Card>

      <Modal
        title={editingCheckpoint ? "แก้ไขจุดตรวจ" : "เพิ่มจุดตรวจใหม่"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={900}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <Form.Item
            name="name"
            label="ชื่อจุดตรวจ"
            rules={[
              { required: true, message: "กรุณาใส่ชื่อจุดตรวจ" },
              { min: 2, message: "ชื่อต้องมีอย่างน้อย 2 ตัวอักษร" },
            ]}
          >
            <Input placeholder="เช่น ประตูหน้า, ลานจอดรถ, อาคารสำนักงาน" />
          </Form.Item>

          <Divider orientation="left">
            <Space>
              <EnvironmentOutlined />
              ตำแหน่งพิกัด GPS
            </Space>
          </Divider>

          <Tabs
            defaultActiveKey="manual"
            items={[
              {
                key: "manual",
                label: (
                  <Space>
                    <AimOutlined />
                    ใส่พิกัดด้วยตนเอง
                  </Space>
                ),
                children: (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="latitude"
                        label="ละติจูด (Latitude)"
                        rules={[
                          { required: true, message: "กรุณาใส่ละติจูด" },
                          {
                            validator: (_, value) => {
                              if (
                                value === null ||
                                value === undefined ||
                                value === ""
                              ) {
                                return Promise.resolve();
                              }
                              // ถ้าเป็นการแก้ไขและค่าไม่เปลี่ยน ให้ผ่าน
                              if (
                                editingCheckpoint &&
                                editingCheckpoint.latitude === value
                              ) {
                                return Promise.resolve();
                              }
                              // ตรวจสอบช่วงสำหรับค่าใหม่
                              if (value < 5.6 || value > 20.5) {
                                return Promise.reject(
                                  new Error(
                                    "ละติจูดต้องอยู่ระหว่าง 5.6 ถึง 20.5 (พิกัดประเทศไทย)"
                                  )
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <InputNumber
                          placeholder="13.736717"
                          style={{ width: "100%" }}
                          precision={6}
                          step={0.000001}
                          onChange={(value) => {
                            if (value !== null && value !== undefined) {
                              setMapPosition((prev) => ({
                                ...prev,
                                lat: value,
                              }));
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="longitude"
                        label="ลองจิจูด (Longitude)"
                        rules={[
                          { required: true, message: "กรุณาใส่ลองจิจูด" },
                          {
                            validator: (_, value) => {
                              if (
                                value === null ||
                                value === undefined ||
                                value === ""
                              ) {
                                return Promise.resolve();
                              }
                              // ถ้าเป็นการแก้ไขและค่าไม่เปลี่ยน ให้ผ่าน
                              if (
                                editingCheckpoint &&
                                editingCheckpoint.longitude === value
                              ) {
                                return Promise.resolve();
                              }
                              // ตรวจสอบช่วงสำหรับค่าใหม่
                              if (value < 97.3 || value > 105.6) {
                                return Promise.reject(
                                  new Error(
                                    "ลองจิจูดต้องอยู่ระหว่าง 97.3 ถึง 105.6 (พิกัดประเทศไทย)"
                                  )
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <InputNumber
                          placeholder="100.523186"
                          style={{ width: "100%" }}
                          precision={6}
                          step={0.000001}
                          onChange={(value) => {
                            if (value !== null && value !== undefined) {
                              setMapPosition((prev) => ({
                                ...prev,
                                lng: value,
                              }));
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: "map",
                label: (
                  <Space>
                    <EnvironmentOutlined />
                    เลือกจากแผนที่
                  </Space>
                ),
                children: (
                  <div style={{ marginTop: 16 }}>
                    <MapPicker
                      value={mapPosition}
                      onChange={handleMapPositionChange}
                    />
                  </div>
                ),
              },
            ]}
          />

          <Form.Item style={{ textAlign: "right", marginTop: 24 }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>ยกเลิก</Button>
              <Button type="primary" htmlType="submit">
                {editingCheckpoint ? "บันทึก" : "สร้าง"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`QR Code - ${selectedCheckpoint?.name}`}
        open={qrModalVisible}
        onCancel={() => setQrModalVisible(false)}
        footer={[
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => downloadQRCode(selectedCheckpoint)}
          >
            ดาวน์โหลด
          </Button>,
          <Button key="close" onClick={() => setQrModalVisible(false)}>
            ปิด
          </Button>,
        ]}
        width={450}
      >
        {selectedCheckpoint && (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <div
              style={{
                background: "#f5f5f5",
                padding: "20px",
                borderRadius: "8px",
                marginBottom: "16px",
                display: "inline-block",
              }}
            >
              {selectedCheckpoint.qr_image ? (
                <img
                  src={selectedCheckpoint.qr_image}
                  alt="QR Code"
                  style={{ maxWidth: "300px", width: "100%" }}
                />
              ) : (
                <QRCode value={selectedCheckpoint.qr_code} size={250} />
              )}
            </div>
            <div style={{ textAlign: "left" }}>
              <p style={{ marginBottom: 8 }}>
                <strong style={{ color: "#1890ff" }}>ชื่อจุดตรวจ:</strong>{" "}
                {selectedCheckpoint.name}
              </p>
              <p style={{ marginBottom: 8 }}>
                <strong style={{ color: "#1890ff" }}>ตำแหน่ง:</strong>{" "}
                {selectedCheckpoint.latitude}, {selectedCheckpoint.longitude}
              </p>
              <p
                style={{
                  marginBottom: 0,
                  wordBreak: "break-all",
                  fontSize: "12px",
                  color: "#666",
                }}
              >
                <strong>QR Code:</strong> {selectedCheckpoint.qr_code}
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Checkpoints;
